import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cx from 'classnames';

import { Funnels, getFunnelSearchString } from 'helpers/funnels/funnels';
import { useUpgradePageTransition } from 'hooks/useUpgradePageTransition';
import { getUser } from 'store/auth/selectors';

import { ReactComponent as LikesPushIcon } from 'assets/icons/simple-dates/likes-funnel.svg';
import { ReactComponent as LikeOutlineIcon } from 'assets/icons/simple-dates/likes-outline.svg';
import { ReactComponent as VisitsOutlineIcon } from 'assets/icons/simple-dates/visits-outline.svg';

import { Default, Mobile } from 'components/helpers/MediaComponents';
import { BottomNavigationBar } from 'components/shared/BottomNavigationBar';
import * as MainPageMobileHeader from 'components/shared/MainPageMobileHeader';
import { DesktopMaxWidthContainer } from 'components/shared/SimpleDates/DesktopMaxWidthContainer';
import { PrivateHeader } from 'components/shared/SimpleDates/PrivateHeader';

import css from './likesAndVisitsLayout.module.sass';

export const LikesAndVisitsLayout: React.FC = ({ children }) => {
  const { t } = useTranslation('common');

  const user = useSelector(getUser);

  const { handleSafePushUpgradePage } = useUpgradePageTransition();

  const handlePushBtnClick = useCallback(() => {
    const search = getFunnelSearchString(Funnels.LikesButton);
    handleSafePushUpgradePage(search);
  }, [handleSafePushUpgradePage]);

  return (
    <>
      <Mobile>
        <div className={css.root}>
          <MainPageMobileHeader.MainPageMobileHeaderTypeSimpleDates
            className={css.header}
            leftBtnIcon={<LikesPushIcon />}
            onLeftBtnClick={!user?.is_premium ? handlePushBtnClick : null}
          />
          <ul className={css.list}>
            <li className={css.item}>
              <NavLink
                to="/likes"
                className={css.link}
                activeClassName={css.activeLink}
              >
                <LikeOutlineIcon className={css.icon} />
                <p className={css.text}>{t('likes')}</p>
                <div className={css.activeLinkLine} />
              </NavLink>
            </li>
            <li className={css.item}>
              <NavLink
                to="/visits"
                className={css.link}
                activeClassName={css.activeLink}
              >
                <VisitsOutlineIcon className={css.icon} />
                <p className={css.text}>{t('visits')}</p>
              </NavLink>
            </li>
          </ul>
          {children}
          <BottomNavigationBar />
        </div>
      </Mobile>

      <Default>
        <div className={cx(css.root, css.rootDesktop)}>
          <PrivateHeader />
          <DesktopMaxWidthContainer>
            <ul className={css.listDesktop}>
              <li className={css.item}>
                <NavLink
                  to="/likes"
                  className={css.link}
                  activeClassName={css.activeLink}
                >
                  <LikeOutlineIcon className={css.icon} />
                  <p className={css.text}>{t('likes')}</p>
                  <div className={css.activeLinkLine} />
                </NavLink>
              </li>
              <li className={css.item}>
                <NavLink
                  to="/visits"
                  className={css.link}
                  activeClassName={css.activeLink}
                >
                  <VisitsOutlineIcon className={css.icon} />
                  <p className={css.text}>{t('visits')}</p>
                </NavLink>
              </li>
            </ul>
            {children}
          </DesktopMaxWidthContainer>
        </div>
      </Default>
    </>
  );
};
